import React from 'react';
import { CustomerListMeta } from '../../types/customer.types';

type CustomersPaginationMeta = Pick<
  CustomerListMeta,
  'current_page' | 'page_size' | 'has_next' | 'has_previous'
> & Partial<Pick<CustomerListMeta, 'total_items' | 'total_pages'>>;

interface PaginationProps {
  meta: CustomersPaginationMeta;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ meta, onPageChange }) => {
  const { current_page, has_previous, has_next } = meta;
  const total_pages = typeof meta.total_pages === 'number' ? meta.total_pages : undefined;
  const total_items = typeof meta.total_items === 'number' ? meta.total_items : undefined;

  // Генерируем массив страниц для отображения
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Максимальное количество видимых страниц
    const halfVisible = Math.floor(maxVisible / 2);

    if (typeof total_pages !== 'number') {
      // Если общее количество страниц неизвестно, не строим список страниц
      return pages;
    }

    if (total_pages <= maxVisible) {
      // Если страниц мало, показываем все
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      // Если страниц много, показываем с многоточием
      if (current_page <= halfVisible + 1) {
        // Начало списка страниц
        for (let i = 1; i <= maxVisible - 2; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(total_pages);
      } else if (current_page >= total_pages - halfVisible) {
        // Конец списка страниц
        pages.push(1);
        pages.push('...');
        for (let i = total_pages - (maxVisible - 3); i <= total_pages; i++) {
          pages.push(i);
        }
      } else {
        // Середина списка страниц
        pages.push(1);
        pages.push('...');
        for (let i = current_page - 1; i <= current_page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(total_pages);
      }
    }

    return pages;
  };

  const pages = generatePageNumbers();
  const showNumeric = typeof total_pages === 'number' && total_pages > 1;

  return (
    <div className="pagination-container">
      {typeof total_items === 'number' && typeof meta.page_size === 'number' ? (
        <div className="pagination-info">
          Показано {((current_page - 1) * meta.page_size) + 1} - {Math.min(current_page * meta.page_size, total_items)} из {total_items}
        </div>
      ) : (
        <div className="pagination-info">Страница {current_page}</div>
      )}
      
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(current_page - 1)}
          disabled={!has_previous}
          aria-label="Предыдущая страница"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {showNumeric ? (
          pages.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                  ...
                </span>
              );
            }

            const pageNumber = page as number;
            return (
              <button
                key={pageNumber}
                className={`pagination-btn ${pageNumber === current_page ? 'active' : ''}`}
                onClick={() => onPageChange(pageNumber)}
                aria-label={`Страница ${pageNumber}`}
                aria-current={pageNumber === current_page ? 'page' : undefined}
              >
                {pageNumber}
              </button>
            );
          })
        ) : (
          <button className={`pagination-btn active`} aria-current="page" disabled>
            {current_page}
          </button>
        )}

        <button
          className="pagination-btn"
          onClick={() => onPageChange(current_page + 1)}
          disabled={!has_next}
          aria-label="Следующая страница"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
